import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Calendar, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DAYS = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

const TIMES = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
];

const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central Europe (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Africa/Lagos', label: 'West Africa (WAT)' },
  { value: 'Africa/Nairobi', label: 'East Africa (EAT)' },
  { value: 'Africa/Johannesburg', label: 'South Africa (SAST)' },
  { value: 'Asia/Dubai', label: 'Gulf (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Seoul', label: 'Korea (KST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST)' },
  { value: 'America/Sao_Paulo', label: 'Brasilia (BRT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)' },
  { value: 'America/Bogota', label: 'Colombia (COT)' },
];

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York';
  }
}

function formatTime12h(time24) {
  const [h, m] = time24.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${m} ${ampm}`;
}

export default function RecapScheduleModal({ onConfirm, onSkip }) {
  const { t } = useTranslation();
  const [day, setDay] = useState('saturday');
  const [time, setTime] = useState('09:00');
  const [tz, setTz] = useState(detectTimezone());

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="recap-schedule-modal">
      <div className="w-full max-w-md theme-surface rounded-3xl shadow-2xl border theme-border overflow-hidden animate-in zoom-in-95 fade-in duration-300">
        {/* Header */}
        <div className="px-6 pt-7 pb-4 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-[var(--theme-accentLight)] flex items-center justify-center mb-4">
            <Calendar className="h-6 w-6 text-[var(--theme-accent)]" />
          </div>
          <h2 className="text-xl font-bold theme-text" data-testid="recap-schedule-title">
            {t('recap_schedule.title', 'Set Your Weekly Recap')}
          </h2>
          <p className="text-sm theme-text-secondary mt-1.5 leading-relaxed">
            {t('recap_schedule.description', "Choose when you'd like to receive your weekly study recap.")}
          </p>
        </div>

        <div className="px-6 pb-6 space-y-5">
          {/* Day picker */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 theme-text-secondary" />
              <label className="text-sm font-medium theme-text-secondary">{t('recap_schedule.day_label', 'Day')}</label>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {DAYS.map(d => (
                <button
                  key={d.value}
                  onClick={() => setDay(d.value)}
                  className={`px-2 py-2 rounded-xl text-xs font-medium transition-all border ${
                    day === d.value
                      ? 'border-[var(--theme-accent)] bg-[var(--theme-accentLight)] theme-text'
                      : 'theme-border theme-surface theme-text-secondary hover:border-[var(--theme-accent)]'
                  }`}
                  data-testid={`recap-day-${d.value}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Time picker */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 theme-text-secondary" />
              <label className="text-sm font-medium theme-text-secondary">{t('recap_schedule.time_label', 'Time')}</label>
            </div>
            <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto pr-1">
              {TIMES.map(tm => (
                <button
                  key={tm}
                  onClick={() => setTime(tm)}
                  className={`px-2 py-2 rounded-xl text-xs font-medium transition-all border ${
                    time === tm
                      ? 'border-[var(--theme-accent)] bg-[var(--theme-accentLight)] theme-text'
                      : 'theme-border theme-surface theme-text-secondary hover:border-[var(--theme-accent)]'
                  }`}
                  data-testid={`recap-time-${tm}`}
                >
                  {formatTime12h(tm)}
                </button>
              ))}
            </div>
          </div>

          {/* Timezone picker */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-4 w-4 theme-text-secondary" />
              <label className="text-sm font-medium theme-text-secondary">{t('recap_schedule.timezone_label', 'Timezone')}</label>
            </div>
            <select
              value={tz}
              onChange={e => setTz(e.target.value)}
              className="w-full rounded-xl theme-surface theme-text theme-border border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--theme-accent)]"
              data-testid="recap-timezone-select"
            >
              {COMMON_TIMEZONES.map(z => (
                <option key={z.value} value={z.value}>{z.label}</option>
              ))}
              {!COMMON_TIMEZONES.find(z => z.value === tz) && (
                <option value={tz}>{tz}</option>
              )}
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={onSkip}
              className="flex-1 rounded-full"
              data-testid="recap-schedule-skip"
            >
              {t('recap_schedule.skip', 'Skip')}
            </Button>
            <Button
              onClick={() => onConfirm({ day, time, timezone: tz })}
              className="flex-1 rounded-full"
              data-testid="recap-schedule-confirm"
            >
              {t('recap_schedule.confirm', 'Confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { DAYS, TIMES, COMMON_TIMEZONES, formatTime12h, detectTimezone };
